import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = '';

// ── Auth: token di sessione + patch globale di fetch ────────────────────────
const getToken = () => localStorage.getItem('auth_token');
const setToken = (t) => t ? localStorage.setItem('auth_token', t) : localStorage.removeItem('auth_token');
if (!window.__authFetchPatched) {
  window.__authFetchPatched = true;
  const _fetch = window.fetch.bind(window);
  window.fetch = async (url, opts = {}) => {
    const isApi = typeof url === 'string' && url.includes('/api/');
    const isAuth = typeof url === 'string' && url.includes('/api/auth/');
    const t = getToken();
    if (isApi && !isAuth && t) opts = { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${t}` } };
    const res = await _fetch(url, opts);
    if (res.status === 401 && isApi && !isAuth) { setToken(null); window.dispatchEvent(new Event('auth-expired')); }
    return res;
  };
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --aqua4: #26c6da;
    --aqua5: #00acc1;
    --aqua6: #00838f;
    --teal:  #00bcd4;
    --glass: rgba(255,255,255,0.55);
    --glass2: rgba(255,255,255,0.78);
    --glass-border: rgba(255,255,255,0.9);
    --shadow: 0 8px 32px rgba(0,172,193,0.12), 0 2px 8px rgba(0,0,0,0.06);
    --shadow-lg: 0 20px 60px rgba(0,172,193,0.18), 0 4px 16px rgba(0,0,0,0.08);
    --text: #1a3a4a;
    --text2: #4a7a8a;
    --text3: #8ab0bc;
    --green: #00c9a7;
    --yellow: #ffb300;
    --red: #ff5252;
    --sans: 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif;
    --mono: 'SF Mono', 'Fira Code', monospace;
    --r: 18px;
    --r-sm: 12px;
    --header-h: 60px;
    --sidebar-w: 290px;
  }

  html { height: 100%; overflow: hidden; }

  body {
    height: 100%;
    overflow: hidden;
    background: linear-gradient(135deg, #e0f7fa 0%, #e8f5e9 40%, #e3f2fd 100%) fixed;
    color: var(--text);
    font-family: var(--sans);
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
  }

  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 600px 400px at 15% 15%, rgba(0,188,212,0.09) 0%, transparent 70%),
      radial-gradient(ellipse 500px 300px at 85% 75%, rgba(0,201,167,0.07) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  #root { height: 100%; overflow: hidden; position: relative; z-index: 1; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(0,172,193,0.3); border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(0,172,193,0.5); }

  .app { display: flex; flex-direction: column; height: 100vh; overflow: hidden; }

  /* HEADER */
  .header {
    height: var(--header-h);
    min-height: var(--header-h);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 28px;
    background: rgba(255,255,255,0.72);
    backdrop-filter: blur(30px) saturate(180%);
    -webkit-backdrop-filter: blur(30px) saturate(180%);
    border-bottom: 1px solid rgba(255,255,255,0.9);
    box-shadow: 0 1px 20px rgba(0,172,193,0.08);
    z-index: 100;
  }

  .header-brand { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 700; letter-spacing: 0.04em; color: var(--aqua6); }
  .brand-icon { width: 32px; height: 32px; background: linear-gradient(135deg, var(--aqua4), var(--teal)); border-radius: 9px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,172,193,0.3); font-size: 16px; }

  .header-tabs { display: flex; gap: 4px; background: rgba(0,172,193,0.08); padding: 4px; border-radius: 12px; }

  .tab-btn { padding: 7px 20px; background: none; border: none; color: var(--text2); font-family: var(--sans); font-size: 13px; font-weight: 600; cursor: pointer; border-radius: 9px; transition: all 0.2s; }
  .tab-btn:hover { color: var(--aqua5); background: rgba(255,255,255,0.6); }
  .tab-btn.active { color: var(--aqua6); background: white; box-shadow: 0 2px 8px rgba(0,172,193,0.2); }

  .header-time { font-family: var(--mono); font-size: 12px; color: var(--text3); background: rgba(0,172,193,0.08); padding: 5px 12px; border-radius: 8px; }

  /* BODY */
  .body { flex: 1; min-height: 0; display: flex; overflow: hidden; }

  /* SIDEBAR */
  .sidebar { width: var(--sidebar-w); min-width: var(--sidebar-w); flex-shrink: 0; display: flex; flex-direction: column; height: 100%; overflow: hidden; border-right: 1px solid rgba(255,255,255,0.8); background: rgba(255,255,255,0.32); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); }

  .sidebar-header { flex-shrink: 0; padding: 14px 12px 10px; border-bottom: 1px solid rgba(0,172,193,0.08); background: rgba(255,255,255,0.45); }

  .sidebar-title-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .sidebar-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: var(--text3); }
  .sidebar-count { font-size: 10px; font-weight: 700; color: white; background: var(--aqua5); padding: 2px 8px; border-radius: 20px; min-width: 24px; text-align: center; }

  .search-box { position: relative; margin-bottom: 8px; }
  .search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12px; pointer-events: none; }
  .search-input { width: 100%; background: rgba(255,255,255,0.85); border: 1.5px solid rgba(0,172,193,0.15); border-radius: 10px; padding: 8px 30px 8px 30px; color: var(--text); font-family: var(--sans); font-size: 12px; font-weight: 500; outline: none; transition: all 0.2s; }
  .search-input::placeholder { color: var(--text3); }
  .search-input:focus { border-color: var(--aqua4); background: white; box-shadow: 0 0 0 3px rgba(0,172,193,0.1); }
  .search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: rgba(0,172,193,0.15); border: none; border-radius: 50%; width: 18px; height: 18px; font-size: 10px; color: var(--aqua5); cursor: pointer; display: flex; align-items: center; justify-content: center; font-weight: 700; }

  .filter-chips { display: flex; gap: 5px; flex-wrap: wrap; }
  .chip { padding: 4px 10px; border-radius: 20px; font-size: 10px; font-weight: 700; cursor: pointer; border: 1.5px solid transparent; transition: all 0.15s; letter-spacing: 0.03em; white-space: nowrap; font-family: var(--sans); }
  .chip-all   { color: var(--aqua5); background: rgba(0,172,193,0.08); border-color: rgba(0,172,193,0.15); }
  .chip-all.active   { background: var(--aqua5); color: white; }
  .chip-online  { color: #00695c; background: rgba(0,201,167,0.08); border-color: rgba(0,201,167,0.2); }
  .chip-online.active  { background: #00897b; color: white; }
  .chip-warning { color: #e65100; background: rgba(255,179,0,0.08); border-color: rgba(255,179,0,0.2); }
  .chip-warning.active { background: #f57c00; color: white; }
  .chip-offline { color: #c62828; background: rgba(255,82,82,0.08); border-color: rgba(255,82,82,0.15); }
  .chip-offline.active { background: #e53935; color: white; }

  .sidebar-list { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 10px 10px; display: flex; flex-direction: column; gap: 7px; }

  .no-results { text-align: center; padding: 32px 12px; color: var(--text3); font-size: 12px; }
  .no-results-icon { font-size: 28px; margin-bottom: 8px; }
  .no-results-text { font-weight: 600; color: var(--text2); margin-bottom: 4px; font-size: 13px; }
  .no-results-sub { font-size: 11px; }

  /* DEVICE CARD */
  .device-card { background: var(--glass2); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: var(--r-sm); padding: 11px 11px 11px 14px; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s; position: relative; overflow: hidden; box-shadow: 0 2px 8px rgba(0,172,193,0.06); flex-shrink: 0; }
  .device-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
  .device-card.status-online::before  { background: linear-gradient(180deg,#00c9a7,#00acc1); }
  .device-card.status-warning::before { background: linear-gradient(180deg,#ffb300,#ff8f00); }
  .device-card.status-offline::before { background: linear-gradient(180deg,#ff5252,#d32f2f); }
  .device-card.status-unknown::before { background: var(--text3); }
  .device-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,172,193,0.14); border-color: rgba(0,188,212,0.35); }
  .device-card.selected { background: rgba(255,255,255,0.95); border-color: var(--aqua4); box-shadow: 0 4px 20px rgba(0,172,193,0.2); }

  .device-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; }
  .device-label { font-size: 12px; font-weight: 700; color: var(--text); }
  .device-plate { font-family: var(--mono); font-size: 9px; color: var(--text3); margin-top: 2px; letter-spacing: 0.08em; background: rgba(0,172,193,0.08); padding: 1px 5px; border-radius: 4px; display: inline-block; }

  .status-badge { display: flex; align-items: center; gap: 4px; font-size: 9px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 8px; border-radius: 20px; flex-shrink: 0; }
  .status-badge.online  { color: #00695c; background: rgba(0,201,167,0.15); }
  .status-badge.warning { color: #e65100; background: rgba(255,179,0,0.15); }
  .status-badge.offline { color: #c62828; background: rgba(255,82,82,0.12); }
  .status-badge.unknown { color: var(--text3); background: rgba(0,0,0,0.06); }
  .status-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .online  .status-dot { background: var(--green); box-shadow: 0 0 6px var(--green); animation: blink 2s infinite; }
  .warning .status-dot { background: var(--yellow); }
  .offline .status-dot { background: var(--red); }
  .unknown .status-dot { background: var(--text3); }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.35} }

  .device-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
  .meta-item { background: rgba(0,172,193,0.06); border-radius: 7px; padding: 4px 7px; }
  .meta-label { font-size: 8px; color: var(--text3); text-transform: uppercase; letter-spacing:.1em; margin-bottom:1px; font-weight:600; }
  .meta-value { font-family: var(--mono); font-size: 10px; color: var(--text); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta-value.teal   { color: var(--aqua5); }
  .meta-value.green  { color: #00897b; }
  .meta-value.yellow { color: #f57c00; }

  /* CONTENT */
  .content { flex: 1; min-width: 0; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }

  /* STAT GRID */
  .stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; }
  .stat-card { background: var(--glass2); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: var(--r); padding: 20px; box-shadow: var(--shadow); transition: transform 0.2s, box-shadow 0.2s; position: relative; overflow: hidden; }
  .stat-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); }
  .stat-value { font-size: 36px; font-weight: 700; line-height: 1; margin-bottom: 4px; letter-spacing: -0.02em; }
  .stat-label { font-size: 11px; color: var(--text3); text-transform: uppercase; letter-spacing: .1em; font-weight: 600; }
  .online-val  { color: #00897b; }
  .warning-val { color: #f57c00; }
  .offline-val { color: #e53935; }
  .total-val   { color: var(--aqua5); }

  /* DEVICE DETAIL PANEL */
  .detail-panel {
    background: var(--glass2);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid var(--glass-border);
    border-radius: var(--r);
    padding: 20px;
    box-shadow: var(--shadow);
  }

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(0,172,193,0.1);
  }

  .detail-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .detail-name { font-size: 16px; font-weight: 700; color: var(--text); }
  .detail-plate { font-family: var(--mono); font-size: 11px; color: var(--text3); background: rgba(0,172,193,0.08); padding: 3px 8px; border-radius: 6px; }

  .detail-actions { display: flex; gap: 8px; align-items: center; }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 16px;
  }

  .detail-item {
    background: rgba(0,172,193,0.05);
    border: 1px solid rgba(0,172,193,0.1);
    border-radius: 10px;
    padding: 10px 12px;
  }

  .detail-item-label {
    font-size: 9px;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 700;
    margin-bottom: 4px;
  }

  .detail-item-value {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text);
    font-weight: 600;
    word-break: break-all;
    line-height: 1.4;
  }

  .detail-item-value.highlight { color: var(--aqua5); }
  .detail-item-value.green { color: #00897b; }
  .detail-item-value.orange { color: #f57c00; }
  .detail-item-value.red { color: #e53935; }

  .detail-section-title {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text3);
    margin-bottom: 8px;
    margin-top: 4px;
  }

  /* CHARTS */
  .chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .chart-card { background: var(--glass2); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: var(--r); padding: 20px; box-shadow: var(--shadow); }
  .chart-title { font-size: 11px; color: var(--text2); text-transform: uppercase; letter-spacing: .1em; margin-bottom: 16px; font-weight: 700; }

  /* SMS PANEL */
  .sms-panel { background: var(--glass2); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: var(--r); padding: 20px; box-shadow: var(--shadow); }
  .panel-title { font-size: 11px; color: var(--text2); text-transform: uppercase; letter-spacing: .1em; font-weight: 700; margin-bottom: 14px; }
  .cmd-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-bottom: 14px; }
  .cmd-btn { background: rgba(0,172,193,0.07); border: 1px solid rgba(0,172,193,0.15); border-radius: var(--r-sm); padding: 10px 12px; font-family: var(--sans); font-size: 12px; color: var(--text2); cursor: pointer; transition: all 0.2s; text-align: left; }
  .cmd-btn:hover { background: rgba(0,172,193,0.14); border-color: var(--aqua4); transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,172,193,0.15); }
  .cmd-name { color: var(--aqua5); font-weight: 700; display: block; margin-bottom: 2px; font-family: var(--mono); font-size: 11px; }
  .cmd-desc { font-size: 10px; color: var(--text3); }
  .sms-input-row { display: flex; gap: 8px; }
  .sms-input { flex: 1; background: rgba(255,255,255,0.85); border: 1.5px solid rgba(0,172,193,0.2); border-radius: var(--r-sm); padding: 10px 14px; color: var(--text); font-family: var(--mono); font-size: 12px; outline: none; transition: all 0.2s; }
  .sms-input:focus { border-color: var(--aqua4); background: white; box-shadow: 0 0 0 3px rgba(0,172,193,0.1); }
  .send-btn { background: linear-gradient(135deg, var(--aqua4), var(--teal)); border: none; border-radius: var(--r-sm); padding: 10px 24px; color: white; font-family: var(--sans); font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 12px rgba(0,172,193,0.3); }
  .send-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,172,193,0.4); }
  .send-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .sms-result { margin-top: 10px; padding: 10px 14px; border-radius: var(--r-sm); font-size: 12px; font-weight: 600; }
  .sms-result.success { background: rgba(0,201,167,0.1); color: #00695c; border: 1px solid rgba(0,201,167,0.25); }
  .sms-result.error   { background: rgba(255,82,82,0.08); color: #c62828; border: 1px solid rgba(255,82,82,0.2); }

  /* EVENTS TABLE */
  .events-card { background: var(--glass2); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: var(--r); overflow: hidden; box-shadow: var(--shadow); }
  .events-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid rgba(0,172,193,0.1); background: rgba(255,255,255,0.5); }
  .events-table { width: 100%; border-collapse: collapse; }
  .events-table th { text-align: left; padding: 10px 20px; font-size: 9px; text-transform: uppercase; letter-spacing: .12em; color: var(--text3); background: rgba(0,172,193,0.04); border-bottom: 1px solid rgba(0,172,193,0.08); font-weight: 700; }
  .events-table td { padding: 10px 20px; border-bottom: 1px solid rgba(0,172,193,0.06); font-size: 12px; color: var(--text2); }
  .events-table tr:last-child td { border-bottom: none; }
  .events-table tr:hover td { background: rgba(0,172,193,0.04); }
  .event-type-badge { display: inline-block; padding: 3px 9px; border-radius: 20px; font-size: 9px; font-weight: 700; letter-spacing: .05em; }
  .event-type-1 { background: rgba(0,172,193,0.1); color: var(--aqua5); }
  .event-type-2 { background: rgba(0,201,167,0.12); color: #00695c; }
  .event-type-4 { background: rgba(255,179,0,0.12); color: #e65100; }

  /* ALERTS */
  .alert-list { display: flex; flex-direction: column; gap: 8px; }
  .alert-item { background: rgba(255,255,255,0.7); border: 1px solid rgba(0,172,193,0.12); border-radius: var(--r-sm); padding: 12px 14px; display: flex; align-items: flex-start; gap: 10px; transition: all 0.2s; }
  .alert-item:hover { background: rgba(255,255,255,0.9); box-shadow: 0 4px 12px rgba(0,172,193,0.1); }
  .alert-message { font-size: 12px; color: var(--text2); flex: 1; }
  .alert-time { font-family: var(--mono); font-size: 10px; color: var(--text3); white-space: nowrap; }

  /* UTILITIES */
  .refresh-btn { background: rgba(0,172,193,0.08); border: 1px solid rgba(0,172,193,0.2); border-radius: 8px; padding: 5px 12px; color: var(--aqua5); font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: var(--sans); }
  .refresh-btn:hover { background: rgba(0,172,193,0.14); }
  .empty-state { text-align: center; padding: 48px 20px; color: var(--text3); font-size: 13px; }
  .pagination { display: flex; align-items: center; gap: 8px; padding: 12px 20px; border-top: 1px solid rgba(0,172,193,0.08); font-size: 11px; color: var(--text3); font-weight: 600; }
  .page-btn { background: rgba(0,172,193,0.08); border: 1px solid rgba(0,172,193,0.15); border-radius: 8px; padding: 4px 12px; color: var(--aqua5); cursor: pointer; font-size: 11px; font-weight: 600; transition: all 0.2s; font-family: var(--sans); }
  .page-btn:hover { background: rgba(0,172,193,0.14); }
  .page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em; color: var(--text2); }

  .export-btn { display: inline-flex; align-items: center; gap: 6px; background: rgba(0,172,193,0.08); border: 1.5px solid rgba(0,172,193,0.2); border-radius: 10px; padding: 8px 16px; color: var(--aqua5); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; font-family: var(--sans); text-decoration: none; letter-spacing: 0.02em; }
  .export-btn:hover { background: rgba(0,172,193,0.15); border-color: var(--aqua4); transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,172,193,0.2); }

  /* EXPORT MODAL */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(26,58,74,0.35);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .modal {
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border: 1px solid var(--glass-border);
    border-radius: 20px;
    padding: 24px;
    width: 420px;
    max-width: 90vw;
    box-shadow: 0 20px 60px rgba(0,172,193,0.25), 0 8px 24px rgba(0,0,0,0.12);
    animation: slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
  .modal-subtitle { font-size: 12px; color: var(--text3); margin-bottom: 20px; }

  .modal-field { margin-bottom: 14px; }
  .modal-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text3); margin-bottom: 6px; display: block; }

  .modal-input {
    width: 100%;
    background: rgba(255,255,255,0.9);
    border: 1.5px solid rgba(0,172,193,0.2);
    border-radius: 10px;
    padding: 10px 12px;
    color: var(--text);
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 500;
    outline: none;
    transition: all 0.2s;
  }
  .modal-input:focus { border-color: var(--aqua4); background: white; box-shadow: 0 0 0 3px rgba(0,172,193,0.1); }

  .modal-quick { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 18px; }
  .quick-chip {
    padding: 5px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;
    cursor: pointer; border: 1.5px solid rgba(0,172,193,0.2);
    background: rgba(0,172,193,0.06); color: var(--aqua5);
    transition: all 0.15s; font-family: var(--sans);
  }
  .quick-chip:hover { background: rgba(0,172,193,0.14); border-color: var(--aqua4); }

  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
  .modal-cancel {
    background: none; border: 1.5px solid rgba(0,172,193,0.2); border-radius: 10px;
    padding: 9px 18px; color: var(--text2); font-family: var(--sans);
    font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  }
  .modal-cancel:hover { background: rgba(0,0,0,0.03); }
  .modal-download {
    background: linear-gradient(135deg, var(--aqua4), var(--teal)); border: none;
    border-radius: 10px; padding: 9px 22px; color: white; font-family: var(--sans);
    font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(0,172,193,0.3); text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .modal-download:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,172,193,0.4); }

  .edit-btn { display: inline-flex; align-items: center; gap: 5px; background: rgba(0,172,193,0.06); border: 1.5px solid rgba(0,172,193,0.15); border-radius: 10px; padding: 8px 14px; color: var(--text2); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; font-family: var(--sans); }
  .edit-btn:hover { background: rgba(0,172,193,0.12); border-color: var(--aqua4); color: var(--aqua5); }
  .edit-btn.active { background: rgba(0,172,193,0.12); border-color: var(--aqua4); color: var(--aqua5); }

  .edit-form {
    background: rgba(0,172,193,0.04);
    border: 1px solid rgba(0,172,193,0.12);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .edit-form-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text3); margin-bottom: 2px; }

  .edit-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }

  .edit-field { display: flex; flex-direction: column; gap: 4px; }
  .edit-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text3); }
  .edit-input { background: rgba(255,255,255,0.9); border: 1.5px solid rgba(0,172,193,0.15); border-radius: 8px; padding: 8px 10px; color: var(--text); font-family: var(--sans); font-size: 12px; font-weight: 500; outline: none; transition: all 0.2s; }
  .edit-input:focus { border-color: var(--aqua4); background: white; box-shadow: 0 0 0 3px rgba(0,172,193,0.1); }

  .edit-actions { display: flex; gap: 8px; justify-content: flex-end; }

  .save-btn { background: linear-gradient(135deg, var(--aqua4), var(--teal)); border: none; border-radius: 8px; padding: 8px 20px; color: white; font-family: var(--sans); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; box-shadow: 0 3px 10px rgba(0,172,193,0.25); }
  .save-btn:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(0,172,193,0.35); }
  .save-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .cancel-btn { background: none; border: 1.5px solid rgba(0,172,193,0.2); border-radius: 8px; padding: 8px 16px; color: var(--text2); font-family: var(--sans); font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cancel-btn:hover { border-color: var(--border2); background: rgba(0,0,0,0.03); }

  .save-feedback { font-size: 11px; font-weight: 600; padding: 6px 12px; border-radius: 8px; }
  .save-feedback.ok { color: #00695c; background: rgba(0,201,167,0.1); border: 1px solid rgba(0,201,167,0.2); }
  .save-feedback.err { color: #c62828; background: rgba(255,82,82,0.08); border: 1px solid rgba(255,82,82,0.2); }
`;

const fmt = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtAgo = (min) => {
  if (min === null) return '—';
  if (min < 1) return 'ora';
  if (min < 60) return `${min}m fa`;
  return `${Math.round(min/60)}h fa`;
};

const COMMANDS = [
  { cmd: 'cpureset', desc: 'Riavvia dispositivo' },
  { cmd: 'getinfo',  desc: 'Info firmware' },
  { cmd: 'getgps',   desc: 'Posizione GPS' },
  { cmd: 'getver',   desc: 'Versione firmware' },
  { cmd: 'getstatus',desc: 'Stato I/O' },
  { cmd: 'ggps',     desc: 'GPS raw' },
];

const PIE_COLORS = ['#00acc1','#00897b','#0097a7','#26c6da','#4db6ac'];

const StatusBadge = ({ status, minutesAgo }) => (
  <span className={`status-badge ${status}`}>
    <span className="status-dot" />
    {status==='online'?'ONLINE':status==='warning'?`${minutesAgo}m`:status==='offline'?'OFFLINE':'N/D'}
  </span>
);

const DeviceCard = ({ device, selected, onClick }) => (
  <div className={`device-card status-${device.status} ${selected?'selected':''}`} onClick={()=>onClick(device)}>
    <div className="device-header">
      <div style={{minWidth:0}}>
        <div className="device-label">{device.label||`Device ${device.imei?.slice(-6)}`}</div>
        {device.vehicle_plate && <div className="device-plate">{device.vehicle_plate}</div>}
      </div>
      <StatusBadge status={device.status} minutesAgo={device.minutesAgo} />
    </div>
    <div className="device-meta">
      <div className="meta-item"><div className="meta-label">Operatore</div><div className="meta-value teal">{device.lastEvent?.operator||'—'}</div></div>
      <div className="meta-item"><div className="meta-label">RAT</div><div className="meta-value green">{device.lastEvent?.rat_type||'—'}</div></div>
      <div className="meta-item"><div className="meta-label">Ultimo</div><div className="meta-value" style={{fontSize:9}}>{fmtAgo(device.minutesAgo)}</div></div>
      <div className="meta-item"><div className="meta-label">24h</div><div className="meta-value yellow">{device.eventCount24h}</div></div>
    </div>
  </div>
);

// Modale per export CSV con selezione intervallo date
const ExportModal = ({ device, onClose }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Imposta intervalli rapidi
  const setQuick = (days) => {
    const now = new Date();
    const start = new Date(now.getTime() - days*24*60*60*1000);
    // formato datetime-local: YYYY-MM-DDTHH:mm
    const fmtLocal = (d) => {
      const off = d.getTimezoneOffset();
      const local = new Date(d.getTime() - off*60000);
      return local.toISOString().slice(0,16);
    };
    setFrom(fmtLocal(start));
    setTo(fmtLocal(now));
  };

  const buildUrl = () => {
    let url = `/api/events/export/${device.imei}`;
    const params = [];
    // datetime-local → ISO con secondi e Z per confronto con timestamp DB
    if (from) params.push(`from=${encodeURIComponent(from + ':00.000Z')}`);
    if (to)   params.push(`to=${encodeURIComponent(to + ':59.999Z')}`);
    if (params.length) url += '?' + params.join('&');
    return url;
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = buildUrl();
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-title">⬇ Esporta CSV</div>
        <div className="modal-subtitle">{device.label||`Device ${device.imei?.slice(-6)}`} — seleziona il periodo</div>

        <div className="modal-quick">
          <button className="quick-chip" onClick={()=>setQuick(1)}>Ultimo giorno</button>
          <button className="quick-chip" onClick={()=>setQuick(7)}>Ultimi 7 giorni</button>
          <button className="quick-chip" onClick={()=>setQuick(30)}>Ultimi 30 giorni</button>
          <button className="quick-chip" onClick={()=>{setFrom('');setTo('');}}>Tutto</button>
        </div>

        <div className="modal-field">
          <label className="modal-label">Data e ora inizio</label>
          <input type="datetime-local" className="modal-input" value={from} onChange={e=>setFrom(e.target.value)}/>
        </div>
        <div className="modal-field">
          <label className="modal-label">Data e ora fine</label>
          <input type="datetime-local" className="modal-input" value={to} onChange={e=>setTo(e.target.value)}/>
        </div>

        <div className="modal-actions">
          <button className="modal-cancel" onClick={onClose}>Annulla</button>
          <button className="modal-download" onClick={download}>⬇ Scarica CSV</button>
        </div>
      </div>
    </div>
  );
};

// Pannello dettaglio SIM completo con form di modifica
const DeviceDetail = ({ device, onDeviceUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ label: '', vehicle_plate: '', vehicle_model: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const ev = device.lastEvent;
  const raw = ev?.raw_json ? (() => { try { return JSON.parse(ev.raw_json); } catch { return {}; } })() : {};

  const openEdit = () => {
    setForm({
      label: device.label || '',
      vehicle_plate: device.vehicle_plate || '',
      vehicle_model: device.vehicle_model || '',
      notes: device.notes || ''
    });
    setFeedback(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const r = await fetch(`/api/devices/${device.imei}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (d.ok) {
        setFeedback({ ok: true, msg: '✓ Salvato' });
        setEditing(false);
        if (onDeviceUpdated) onDeviceUpdated();
      } else {
        setFeedback({ ok: false, msg: 'Errore salvataggio' });
      }
    } catch(e) {
      setFeedback({ ok: false, msg: e.message });
    }
    setSaving(false);
  };

  const fields = [
    { label: 'IMEI',           value: device.imei,            cls: 'highlight' },
    { label: 'ICCID',          value: device.iccid,           cls: 'highlight' },
    { label: 'IMSI',           value: ev?.imsi||raw?.imsi?.imsi, cls: '' },
    { label: 'Veicolo',        value: device.label,           cls: '' },
    { label: 'Targa',          value: device.vehicle_plate,   cls: '' },
    { label: 'Modello',        value: device.vehicle_model,   cls: '' },
    { label: 'Operatore',      value: ev?.operator,           cls: 'highlight' },
    { label: 'MNC',            value: ev?.mnc,                cls: '' },
    { label: 'MCC',            value: ev?.mcc,                cls: '' },
    { label: 'RAT Type',       value: ev?.rat_type,           cls: 'green' },
    { label: 'IP Address',     value: ev?.ip_address,         cls: '' },
    { label: 'Ultimo evento',  value: ev?.event_type_desc,    cls: '' },
    { label: 'MME Node',       value: ev?.mme,                cls: '' },
    { label: 'VLR',            value: ev?.vlr,                cls: '' },
    { label: 'Stato',          value: device.status?.toUpperCase(), cls: device.status==='online'?'green':device.status==='offline'?'red':'orange' },
    { label: 'Ultimo segnale', value: device.minutesAgo!==null?`${device.minutesAgo} minuti fa`:'—', cls: '' },
    { label: 'Eventi 24h',     value: device.eventCount24h,  cls: '' },
    { label: 'Cambi rete 1h',  value: device.operatorSwitches1h, cls: '' },
  ];

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <div className="detail-title">
          <StatusBadge status={device.status} minutesAgo={device.minutesAgo} />
          <div className="detail-name">{device.label||`Device ${device.imei?.slice(-6)}`}</div>
          {device.vehicle_plate && <div className="detail-plate">{device.vehicle_plate}</div>}
        </div>
        <div className="detail-actions">
          {feedback && <span className={`save-feedback ${feedback.ok?'ok':'err'}`}>{feedback.msg}</span>}
          <button className={`edit-btn ${editing?'active':''}`} onClick={editing?()=>setEditing(false):openEdit}>
            {editing ? '✕ Annulla' : '✏️ Modifica'}
          </button>
          <a className="export-btn" href="#" onClick={(e)=>{e.preventDefault();setShowExport(true);}}>⬇ CSV</a>
        </div>
      </div>

      {editing && (
        <div className="edit-form">
          <div className="edit-form-title">Modifica dati dispositivo</div>
          <div className="edit-row">
            <div className="edit-field">
              <div className="edit-label">Nome / Veicolo</div>
              <input className="edit-input" value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))} placeholder="es. FIAT Ducato"/>
            </div>
            <div className="edit-field">
              <div className="edit-label">Targa</div>
              <input className="edit-input" value={form.vehicle_plate} onChange={e=>setForm(f=>({...f,vehicle_plate:e.target.value.toUpperCase()}))} placeholder="es. AB123CD"/>
            </div>
            <div className="edit-field">
              <div className="edit-label">Modello</div>
              <input className="edit-input" value={form.vehicle_model} onChange={e=>setForm(f=>({...f,vehicle_model:e.target.value}))} placeholder="es. Fiat Ducato 2.3"/>
            </div>
          </div>
          <div className="edit-field">
            <div className="edit-label">Note</div>
            <input className="edit-input" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Note tecniche, installazione, ecc."/>
          </div>
          <div className="edit-actions">
            <button className="cancel-btn" onClick={()=>setEditing(false)}>Annulla</button>
            <button className="save-btn" onClick={saveEdit} disabled={saving}>{saving?'Salvataggio...':'💾 Salva'}</button>
          </div>
        </div>
      )}

      <div className="detail-section-title">Dati SIM e dispositivo</div>
      <div className="detail-grid">
        {fields.map((f,i) => (
          <div key={i} className="detail-item">
            <div className="detail-item-label">{f.label}</div>
            <div className={`detail-item-value ${f.cls||''}`}>{f.value||'—'}</div>
          </div>
        ))}
      </div>

      {showExport && <ExportModal device={device} onClose={()=>setShowExport(false)} />}
    </div>
  );
};

function Login({ onAuthed }) {
  const [step, setStep] = useState('password');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [loginId, setLoginId] = useState(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const doLogin = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/auth/login`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({username,password}) });
      const d = await r.json();
      if (r.ok && d.loginId) { setLoginId(d.loginId); setStep('otp'); }
      else if (d.disabled) { onAuthed(); }
      else setErr(d.error || 'Accesso non riuscito');
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };
  const doVerify = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/auth/verify`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({loginId,code}) });
      const d = await r.json();
      if (r.ok && d.token) { setToken(d.token); onAuthed(); }
      else setErr(d.error || 'Codice non valido');
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div className="sms-panel" style={{width:'100%',maxWidth:380}}>
        <div style={{textAlign:'center',marginBottom:18}}>
          <div style={{fontSize:32}}>📡</div>
          <div style={{fontWeight:700,fontSize:18,letterSpacing:'0.04em'}}>1NCE MONITOR</div>
          <div style={{fontSize:12,color:'var(--text3)'}}>Accesso protetto</div>
        </div>
        {step==='password' ? (
          <>
            <input className="sms-input" style={{width:'100%',marginBottom:10}} placeholder="Username" value={username} onChange={e=>setUsername(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doLogin()}/>
            <input className="sms-input" style={{width:'100%',marginBottom:10}} type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doLogin()}/>
            <button className="send-btn" style={{width:'100%'}} onClick={doLogin} disabled={loading||!password}>{loading?'Invio codice…':'Accedi'}</button>
          </>
        ) : (
          <>
            <div style={{fontSize:12,color:'var(--text2)',marginBottom:10,textAlign:'center'}}>Codice inviato su Telegram. Inseriscilo qui sotto (valido 5 min).</div>
            <input className="sms-input" autoFocus inputMode="numeric" maxLength={6}
              style={{width:'100%',marginBottom:10,textAlign:'center',letterSpacing:'0.3em',fontSize:18}}
              placeholder="••••••" value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,''))} onKeyDown={e=>e.key==='Enter'&&doVerify()}/>
            <button className="send-btn" style={{width:'100%'}} onClick={doVerify} disabled={loading||code.length<6}>{loading?'Verifica…':'Verifica codice'}</button>
            <button className="refresh-btn" style={{width:'100%',marginTop:8}} onClick={()=>{setStep('password');setCode('');setErr(null);}}>← Indietro</button>
          </>
        )}
        {err && <div className="sms-result error" style={{marginTop:10}}>{err}</div>}
      </div>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(null);   // null=verifica, false=login, true=ok
  const [tab, setTab] = useState('dashboard');
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(0);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [smsPayload, setSmsPayload] = useState('');
  const [smsResult, setSmsResult] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [time, setTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showExportEvents, setShowExportEvents] = useState(false);
  const [fleetRows, setFleetRows] = useState([]);
  const [fleetLastSync, setFleetLastSync] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [fleetSearch, setFleetSearch] = useState('');
  const [fotaCsvInfo, setFotaCsvInfo] = useState(null);
  const [csvMsg, setCsvMsg] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);

  useEffect(()=>{const t=setInterval(()=>setTime(new Date()),1000);return()=>clearInterval(t)},[]);

  // Verifica autenticazione al mount + ascolta scadenza token
  useEffect(()=>{
    (async()=>{
      try{
        const s=await (await fetch(`${API}/api/auth/status`)).json();
        if(!s.authEnabled){setAuthed(true);return;}
        if(!getToken()){setAuthed(false);return;}
        const r=await fetch(`${API}/api/auth/me`);
        setAuthed(r.ok);
      }catch(e){setAuthed(false);}
    })();
    const onExp=()=>setAuthed(false);
    window.addEventListener('auth-expired',onExp);
    return()=>window.removeEventListener('auth-expired',onExp);
  },[]);

  const logout=()=>{setToken(null);setAuthed(false);};

  const fetchFleet = useCallback(async()=>{
    try{const r=await fetch(`${API}/api/fleet`);const d=await r.json();setFleetRows(d.rows||[]);setFleetLastSync(d.lastSync||null);}catch(e){}
  },[]);

  const fetchFotaCsvInfo = useCallback(async()=>{
    try{const r=await fetch(`${API}/api/fleet/fota-csv`);setFotaCsvInfo(await r.json());}catch(e){}
  },[]);

  const handleFotaCsv = useCallback(async(file)=>{
    if(!file)return;
    setUploadingCsv(true);setCsvMsg(null);
    try{
      const text=await file.text();
      const r=await fetch(`${API}/api/fleet/fota-csv`,{method:'POST',headers:{'Content-Type':'text/csv'},body:text});
      const d=await r.json();
      if(r.ok){setCsvMsg({ok:true,msg:`✓ CSV importato: ${d.count} dispositivi. Premi "Ricostruisci database" per applicare.`});fetchFotaCsvInfo();}
      else setCsvMsg({ok:false,msg:d.error||'Errore import'});
    }catch(e){setCsvMsg({ok:false,msg:e.message});}
    setUploadingCsv(false);
  },[fetchFotaCsvInfo]);

  const startFleetSync = useCallback(async()=>{
    setSyncing(true);
    try{
      await fetch(`${API}/api/fleet/sync`,{method:'POST'});
      const poll=async()=>{
        const r=await fetch(`${API}/api/fleet/sync/status`);const st=await r.json();setSyncStatus(st);
        if(st.running){setTimeout(poll,1500);}
        else{setSyncing(false);fetchFleet();}
      };
      poll();
    }catch(e){setSyncing(false);setSyncStatus({phase:'error',message:e.message});}
  },[fetchFleet]);

  const fetchDevices = useCallback(async()=>{
    try{const r=await fetch(`${API}/api/devices`);const d=await r.json();setDevices(d);if(d.length>0&&!selected)setSelected(d[0]);setLoading(false);}catch(e){setLoading(false);}
  },[selected]);

  const fetchEvents = useCallback(async()=>{
    if(!selected)return;
    const r=await fetch(`${API}/api/events?imei=${selected.imei}&limit=30&offset=${eventsPage*30}`);
    const d=await r.json();setEvents(d.events||[]);setEventsTotal(d.total||0);
  },[selected,eventsPage]);

  const fetchStats = useCallback(async()=>{
    if(!selected)return;
    const r=await fetch(`${API}/api/stats/${selected.imei}`);setStats(await r.json());
  },[selected]);

  const fetchAlerts = useCallback(async()=>{
    const url=selected?`${API}/api/alerts?imei=${selected.imei}&limit=20`:`${API}/api/alerts?limit=20`;
    const r=await fetch(url);setAlerts(await r.json());
  },[selected]);

  useEffect(()=>{if(authed===true){fetchDevices();fetchFleet();fetchFotaCsvInfo();}},[authed]);
  useEffect(()=>{if(selected){fetchEvents();fetchStats();fetchAlerts();}},[selected,eventsPage]);
  useEffect(()=>{
    const t=setInterval(()=>{fetchDevices();if(selected){fetchEvents();fetchStats();fetchAlerts();}},30000);
    return()=>clearInterval(t);
  },[selected]);

  const sendSMS = async()=>{
    if(!smsPayload||!selected)return;
    setSmsSending(true);setSmsResult(null);
    try{
      const r=await fetch(`${API}/api/devices/${selected.imei}/sms`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({payload:smsPayload})});
      const d=await r.json();
      setSmsResult(d.success?{ok:true,msg:'✓ SMS inviato con successo'}:{ok:false,msg:d.error||'Errore invio'});
    }catch(e){setSmsResult({ok:false,msg:e.message});}
    setSmsSending(false);
  };

  const online  = devices.filter(d=>d.status==='online').length;
  const warning = devices.filter(d=>d.status==='warning').length;
  const offline = devices.filter(d=>d.status==='offline').length;

  const filteredDevices = devices.filter(d => {
    const matchStatus = filterStatus==='all' || d.status===filterStatus;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (d.label||'').toLowerCase().includes(q) ||
      (d.vehicle_plate||'').toLowerCase().includes(q) ||
      (d.vehicle_model||'').toLowerCase().includes(q) ||
      (d.imei||'').includes(q) ||
      (d.lastEvent?.operator||'').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  if (authed !== true) {
    return (
      <>
        <style>{css}</style>
        {authed===null
          ? <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)'}}>Caricamento…</div>
          : <Login onAuthed={()=>setAuthed(true)}/>}
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <header className="header">
          <div className="header-brand"><div className="brand-icon">📡</div>1NCE MONITOR</div>
          <div className="header-tabs">
            {['dashboard','events','alerts','fleet'].map(t=>(
              <button key={t} className={`tab-btn ${tab===t?'active':''}`} onClick={()=>setTab(t)}>
                {t==='dashboard'?'Dashboard':t==='events'?'Storico':t==='alerts'?'Alert':'Database Flotta'}
              </button>
            ))}
          </div>
          <div className="header-time" style={{display:'flex',alignItems:'center',gap:10}}>
            <span>{time.toLocaleString('it-IT',{timeZone:'Europe/Rome',hour:'2-digit',minute:'2-digit',second:'2-digit'})} · Roma</span>
            <button className="tab-btn" onClick={logout} title="Esci">⎋ Esci</button>
          </div>
        </header>

        <div className="body">
          <aside className="sidebar">
            <div className="sidebar-header">
              <div className="sidebar-title-row">
                <div className="sidebar-title">Dispositivi</div>
                <div className="sidebar-count">{filteredDevices.length}/{devices.length}</div>
              </div>
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input className="search-input" placeholder="Cerca targa, operatore, IMEI..." value={search} onChange={e=>setSearch(e.target.value)}/>
                {search && <button className="search-clear" onClick={()=>setSearch('')}>✕</button>}
              </div>
              <div className="filter-chips">
                {[{key:'all',label:'Tutti'},{key:'online',label:`Online ${online}`},{key:'warning',label:`Warn ${warning}`},{key:'offline',label:`Off ${offline}`}].map(f=>(
                  <button key={f.key} className={`chip chip-${f.key} ${filterStatus===f.key?'active':''}`} onClick={()=>setFilterStatus(f.key)}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="sidebar-list">
              {loading ? (
                <div className="no-results"><div className="no-results-icon">⏳</div><div className="no-results-text">Caricamento...</div></div>
              ) : filteredDevices.length===0 ? (
                <div className="no-results">
                  <div className="no-results-icon">🔎</div>
                  <div className="no-results-text">Nessun risultato</div>
                  <div className="no-results-sub">{search?`"${search}" non trovato`:'Nessun dispositivo in questa categoria'}</div>
                  {(search||filterStatus!=='all')&&(<button className="refresh-btn" style={{marginTop:12}} onClick={()=>{setSearch('');setFilterStatus('all');}}>Rimuovi filtri</button>)}
                </div>
              ) : (
                filteredDevices.map(d=><DeviceCard key={d.imei} device={d} selected={selected?.imei===d.imei} onClick={setSelected}/>)
              )}
            </div>
          </aside>

          <main className="content">
            {tab==='dashboard'&&(
              <>
                {/* STAT CARDS */}
                <div className="stat-grid">
                  <div className="stat-card"><div className="stat-value online-val">{online}</div><div className="stat-label">Online</div></div>
                  <div className="stat-card"><div className="stat-value warning-val">{warning}</div><div className="stat-label">Warning</div></div>
                  <div className="stat-card"><div className="stat-value offline-val">{offline}</div><div className="stat-label">Offline</div></div>
                  <div className="stat-card"><div className="stat-value total-val">{devices.length}</div><div className="stat-label">Totale SIM</div></div>
                </div>

                {/* DEVICE DETAIL */}
                {selected&&(
                  <DeviceDetail
                    device={selected}
                    onDeviceUpdated={fetchDevices}
                  />
                )}

                {/* CHARTS */}
                {selected&&stats&&stats.operators?.length>0&&(
                  <div className="chart-grid">
                    <div className="chart-card">
                      <div className="chart-title">Operatori 24h</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={stats.operators} dataKey="count" nameKey="operator" cx="50%" cy="50%" outerRadius={60} label={({operator,percent})=>`${operator} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                            {stats.operators.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                          </Pie>
                          <Tooltip contentStyle={{background:'rgba(255,255,255,0.96)',border:'1px solid rgba(0,172,193,0.2)',borderRadius:12,fontSize:11}}/>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="chart-card">
                      <div className="chart-title">Eventi per ora — 24h</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={stats.byHour}>
                          <XAxis dataKey="hour" tick={{fontSize:9,fill:'#8ab0bc'}}/>
                          <YAxis tick={{fontSize:9,fill:'#8ab0bc'}}/>
                          <Tooltip contentStyle={{background:'rgba(255,255,255,0.96)',border:'1px solid rgba(0,172,193,0.2)',borderRadius:12,fontSize:11}}/>
                          <Bar dataKey="count" fill="url(#barGrad)" radius={[4,4,0,0]}/>
                          <defs><linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#26c6da"/><stop offset="100%" stopColor="#00acc1" stopOpacity={0.7}/></linearGradient></defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* SMS PANEL */}
                {selected&&(
                  <div className="sms-panel">
                    <div className="panel-title">Invia comando — {selected.label||`Device ${selected.imei?.slice(-6)}`}</div>
                    <div className="cmd-grid">
                      {COMMANDS.map(c=>(
                        <button key={c.cmd} className="cmd-btn" onClick={()=>setSmsPayload(c.cmd)}>
                          <span className="cmd-name">{c.cmd}</span>
                          <span className="cmd-desc">{c.desc}</span>
                        </button>
                      ))}
                    </div>
                    <div className="sms-input-row">
                      <input className="sms-input" value={smsPayload} onChange={e=>setSmsPayload(e.target.value)} placeholder="comando personalizzato..." onKeyDown={e=>e.key==='Enter'&&sendSMS()}/>
                      <button className="send-btn" onClick={sendSMS} disabled={smsSending||!smsPayload}>{smsSending?'Invio...':'Invia'}</button>
                    </div>
                    {smsResult&&<div className={`sms-result ${smsResult.ok?'success':'error'}`}>{smsResult.msg}</div>}
                  </div>
                )}
              </>
            )}

            {tab==='events'&&(
              <div className="events-card">
                <div className="events-header">
                  <div className="section-title">Storico {selected?`— ${selected.label||`Device ${selected.imei?.slice(-6)}`}`:''} <span style={{color:'var(--text3)',fontWeight:400}}>({eventsTotal})</span></div>
                  <div style={{display:'flex',gap:8}}>
                    {selected&&<a className="export-btn" href="#" onClick={(e)=>{e.preventDefault();setShowExportEvents(true);}}>⬇ CSV</a>}
                    <button className="refresh-btn" onClick={fetchEvents}>↻ Aggiorna</button>
                  </div>
                </div>
                {events.length===0?<div className="empty-state">Nessun evento trovato</div>:(
                  <table className="events-table">
                    <thead><tr><th>Timestamp</th><th>Tipo</th><th>Operatore</th><th>RAT</th><th>IP</th><th>MME</th></tr></thead>
                    <tbody>
                      {events.map(ev=>(
                        <tr key={ev.id}>
                          <td style={{fontFamily:'var(--mono)',fontSize:11}}>{fmt(ev.timestamp)}</td>
                          <td><span className={`event-type-badge event-type-${ev.event_type_id}`}>{ev.event_type_desc||`type ${ev.event_type_id}`}</span></td>
                          <td style={{color:'var(--aqua5)',fontWeight:600}}>{ev.operator||'—'}</td>
                          <td style={{fontFamily:'var(--mono)',fontSize:11,color:'#00897b',fontWeight:600}}>{ev.rat_type||'—'}</td>
                          <td style={{fontFamily:'var(--mono)',fontSize:11}}>{ev.ip_address||'—'}</td>
                          <td style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--text3)',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.mme||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="pagination">
                  <button className="page-btn" onClick={()=>setEventsPage(p=>p-1)} disabled={eventsPage===0}>← Prec</button>
                  <span>Pagina {eventsPage+1} di {Math.ceil(eventsTotal/30)||1}</span>
                  <button className="page-btn" onClick={()=>setEventsPage(p=>p+1)} disabled={(eventsPage+1)*30>=eventsTotal}>Succ →</button>
                </div>
                {showExportEvents && selected && <ExportModal device={selected} onClose={()=>setShowExportEvents(false)} />}
              </div>
            )}

            {tab==='alerts'&&(
              <div className="events-card">
                <div className="events-header">
                  <div className="section-title">Alert inviati</div>
                  <button className="refresh-btn" onClick={fetchAlerts}>↻ Aggiorna</button>
                </div>
                <div style={{padding:16}}>
                  {alerts.length===0?<div className="empty-state">Nessun alert inviato</div>:(
                    <div className="alert-list">
                      {alerts.map(a=>(
                        <div key={a.id} className="alert-item">
                          <span style={{fontSize:18}}>{a.alert_type==='offline'?'🔴':a.alert_type==='network_instability'?'⚠️':'🚨'}</span>
                          <div className="alert-message">
                            <div style={{fontSize:10,color:'var(--text3)',marginBottom:3,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em'}}>{a.alert_type}</div>
                            {a.message?.replace(/<[^>]+>/g,'')||'—'}
                          </div>
                          <span className="alert-time">{fmt(a.sent_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab==='fleet'&&(()=>{
              const matchBadge=(m)=>m==='matched_by_iccid'?{t:'ICCID',c:'#1aa06b'}:m==='matched_by_imei'?{t:'IMEI',c:'#0096c7'}:m==='matched_by_stream'?{t:'Stream',c:'#8a63d2'}:{t:'No SIM',c:'#c8284a'};
              const q=fleetSearch.toLowerCase();
              const rows=fleetRows.filter(r=>!q||Object.values(r).some(v=>String(v??'').toLowerCase().includes(q)));
              const ph={fota:'FOTAweb',navixy:'Navixy','1nce':'1NCE',merge:'Merge',done:'Completato',error:'Errore'};
              // Tutte le colonne della flotta, in ordine logico
              const COLS=[
                ['activity_status','Stato'],['description','Veicolo / Desc.'],['imei','IMEI'],['serial','Serial'],
                ['model','Modello'],['model_platform','Piattaforma'],['product_code','Product code'],
                ['current_firmware','Firmware'],['current_configuration','Configurazione'],['modem_version','Modem'],
                ['bootloader','Bootloader'],['task_queue','Task'],['company_name','Azienda'],['group_name','Gruppo'],
                ['vin','VIN'],['gsm_number','GSM'],['iccid','ICCID'],['imsi','IMSI'],
                ['seen_at','Visto'],['created_at','Creato'],['updated_at','Aggiornato'],
                ['1nce_status','1NCE Stato'],['1nce_label','1NCE Label'],['1nce_iccid','1NCE ICCID'],['1nce_imei','1NCE IMEI'],
                ['1nce_msisdn','MSISDN'],['1nce_ip_address','IP 1NCE'],['1nce_sim_type','SIM type'],['1nce_imei_lock','IMEI lock'],
                ['1nce_data_quota_left','Quota Dati'],['1nce_sms_quota_left','Quota SMS'],['1nce_tariff_detail','Tariffa'],
                ['1nce_end_date','Scadenza'],['1nce_pin','PIN'],['1nce_pin2','PIN2'],['1nce_puk','PUK'],['1nce_puk2','PUK2'],['1nce_eid','EID'],
              ];
              const MONO=new Set(['imei','serial','iccid','imsi','gsm_number','1nce_iccid','1nce_imei','1nce_msisdn','1nce_ip_address','1nce_pin','1nce_pin2','1nce_puk','1nce_puk2','1nce_eid']);
              const thStyle={position:'sticky',top:0,zIndex:2,background:'var(--thead-bg,var(--panel-bg))',whiteSpace:'nowrap',fontSize:11,padding:'7px 10px'};
              const fmtCell=(k,v)=>{if(v==null||v==='')return '—';if(k==='1nce_imei_lock')return v==1?'Sì':'No';return String(v);};
              return (
              <div className="events-card">
                <div className="events-header">
                  <div className="section-title">Database Flotta <span style={{color:'var(--text3)',fontWeight:400}}>({fleetRows.length})</span></div>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                    {fleetLastSync&&<span style={{fontSize:11,color:'var(--text3)'}}>Ultimo sync: {fmt(fleetLastSync.synced_at)}</span>}
                    <label className="refresh-btn" style={{cursor:uploadingCsv?'wait':'pointer',margin:0}}>
                      {uploadingCsv?'Caricamento…':'📄 Carica CSV FOTA'}
                      <input type="file" accept=".csv,text/csv" style={{display:'none'}} disabled={uploadingCsv}
                        onChange={e=>{const f=e.target.files?.[0];handleFotaCsv(f);e.target.value='';}}/>
                    </label>
                    <button className="send-btn" onClick={startFleetSync} disabled={syncing}>{syncing?'Sincronizzazione…':'🔄 Ricostruisci database'}</button>
                  </div>
                </div>
                {(csvMsg||fotaCsvInfo?.count>0)&&(
                  <div style={{padding:'4px 16px',fontSize:11}}>
                    {csvMsg&&<div className={`sms-result ${csvMsg.ok?'success':'error'}`} style={{marginBottom:6}}>{csvMsg.msg}</div>}
                    {fotaCsvInfo?.count>0&&<span style={{color:'var(--text3)'}}>CSV FOTA in archivio: <b>{fotaCsvInfo.count}</b> dispositivi · importato {fmt(fotaCsvInfo.imported_at)}</span>}
                  </div>
                )}

                {syncing&&syncStatus&&(
                  <div style={{padding:'12px 16px'}}>
                    <div style={{fontSize:12,marginBottom:6,color:'var(--text2)'}}>
                      [{ph[syncStatus.phase]||syncStatus.phase}] {syncStatus.message}
                    </div>
                    <div style={{height:8,background:'rgba(0,150,200,0.12)',borderRadius:6,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${syncStatus.total?Math.round(syncStatus.progress/syncStatus.total*100):8}%`,background:'linear-gradient(90deg,#26c6da,#00acc1)',transition:'width .3s'}}/>
                    </div>
                  </div>
                )}
                {!syncing&&syncStatus?.phase==='error'&&(
                  <div className="sms-result error" style={{margin:'12px 16px'}}>Errore sync: {syncStatus.message}</div>
                )}
                {!syncing&&fleetLastSync?.stats&&(
                  <div style={{padding:'8px 16px',fontSize:11,color:'var(--text3)',display:'flex',gap:14,flexWrap:'wrap'}}>
                    <span>FOTAweb: <b>{fleetLastSync.stats.fota}</b></span>
                    <span>Navixy: <b>{fleetLastSync.stats.navixy}</b></span>
                    <span>SIM 1NCE: <b>{fleetLastSync.stats.sims}</b></span>
                    <span>Abbinate ICCID: <b>{fleetLastSync.stats.matched_iccid}</b></span>
                    <span>Abbinate IMEI: <b>{fleetLastSync.stats.matched_imei}</b></span>
                    <span>Da stream: <b>{fleetLastSync.stats.matched_stream||0}</b></span>
                    <span>Senza SIM: <b>{fleetLastSync.stats.no_sim}</b></span>
                  </div>
                )}

                <div style={{padding:'8px 16px'}}>
                  <input className="sms-input" placeholder="Cerca IMEI, veicolo, ICCID, MSISDN, label…" value={fleetSearch} onChange={e=>setFleetSearch(e.target.value)} style={{width:'100%'}}/>
                </div>

                {fleetRows.length===0?(
                  <div className="empty-state">Nessun dato. Carica un CSV FOTA e/o premi “Ricostruisci database”.</div>
                ):(
                  <div style={{margin:'0 16px 12px',overflow:'auto',maxHeight:'calc(100vh - 360px)',border:'1px solid var(--border,rgba(0,150,200,0.2))',borderRadius:8}}>
                    <table className="events-table" style={{minWidth:'max-content',width:'100%'}}>
                      <thead><tr>
                        <th style={{...thStyle,left:0,zIndex:3}}>SIM</th>
                        {COLS.map(([k,label])=><th key={k} style={thStyle}>{label}</th>)}
                      </tr></thead>
                      <tbody>
                        {rows.map((r,i)=>{const b=matchBadge(r.match_status);return(
                          <tr key={r.id||i}>
                            <td style={{position:'sticky',left:0,zIndex:1,background:'var(--table-bg,var(--panel-bg))'}}>
                              <span style={{fontSize:10,fontWeight:700,color:b.c,border:`1px solid ${b.c}`,borderRadius:6,padding:'1px 6px',whiteSpace:'nowrap'}}>{b.t}</span>
                            </td>
                            {COLS.map(([k])=>(
                              <td key={k} style={{whiteSpace:'nowrap',fontSize:11,padding:'6px 10px',
                                fontFamily:MONO.has(k)?'var(--mono)':'inherit',
                                fontWeight:k==='description'?600:400,
                                maxWidth:280,overflow:'hidden',textOverflow:'ellipsis'}} title={fmtCell(k,r[k])}>
                                {fmtCell(k,r[k])}
                              </td>
                            ))}
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              );
            })()}
          </main>
        </div>
      </div>
    </>
  );
}
